import { useState, useEffect, useCallback } from 'react';
import { 
  ChevronRight, 
  ChevronDown, 
  Search, 
  Plus, 
  Edit3, 
  Trash2, 
  MoreHorizontal,
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock,
  Eye
} from 'lucide-react';
import { BlueprintTreeService } from '../../services/blueprintTreeService';
import { logger } from '../../services/loggingService';
import type { BlueprintTreeNode, BlueprintTreeData } from '../../services/blueprintTreeService';

interface PromptSidebarProps {
  projectId: string;
  onNodeSelect: (node: BlueprintTreeNode, data: any, promptContext: string) => void;
  onNodeUpdate: (nodeId: string, updatedData: any) => void;
  selectedNodeId?: string;
  className?: string;
}

export function PromptSidebar({ 
  projectId, 
  onNodeSelect, 
  onNodeUpdate, 
  selectedNodeId,
  className = '' 
}: PromptSidebarProps) {
  const [treeData, setTreeData] = useState<BlueprintTreeData | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set(['root']));
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<BlueprintTreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadBlueprintTree = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const tree = await BlueprintTreeService.buildBlueprintTree(projectId);
      setTreeData(tree);
      
      // Auto-expand first level
      setExpandedNodes(new Set(['root', 'requirements', 'architecture', 'conventions', 'security', 'design', 'phases']));
    } catch (err) {
      logger.error('Error loading blueprint tree', { error: err, projectId });
      setError('Failed to load blueprint tree');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const handleSearch = useCallback(async (query: string) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      const results = await BlueprintTreeService.searchBlueprintTree(projectId, query);
      setSearchResults(results);
    } catch (err) {
      logger.error('Error searching blueprint tree', { error: err, projectId, query });
    }
  }, [projectId]);

  const handleNodeClick = useCallback(async (node: BlueprintTreeNode) => {
    try {
      const { data, promptContext } = await BlueprintTreeService.getNodeData(node.id, projectId);
      onNodeSelect(node, data, promptContext);
    } catch (err) {
      logger.error('Error loading node data', { error: err, nodeId: node.id, projectId });
    }
  }, [projectId, onNodeSelect]);

  const toggleNodeExpansion = useCallback((nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  }, []);

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'complete':
        return <CheckCircle className="w-4 h-4 text-success" />;
      case 'in_progress':
        return <Clock className="w-4 h-4 text-warning" />;
      case 'needs_review':
        return <AlertCircle className="w-4 h-4 text-warning" />;
      default:
        return <div className="w-4 h-4 rounded-full border-2 border-border" />;
    }
  };

  const renderTreeNode = (node: BlueprintTreeNode, depth: number = 0) => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children && node.children.length > 0;
    const isSelected = selectedNodeId === node.id;
    const indentClass = `ml-${depth * 4}`;

    return (
      <div key={node.id} className={`${indentClass}`}>
        <div
          className={`
            flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer group hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors
            ${isSelected ? 'bg-accent-100 text-accent-900' : 'text-primary'}
          `}
          onClick={() => {
            if (hasChildren) {
              toggleNodeExpansion(node.id);
            } else {
              handleNodeClick(node);
            }
          }}
        >
          {/* Expand/Collapse Icon */}
          {hasChildren ? (
            <button
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                toggleNodeExpansion(node.id);
              }}
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          ) : (
            <div className="w-6" /> // Spacer for alignment
          )}

          {/* Node Icon */}
          <span className="text-lg">{node.icon}</span>

          {/* Node Label */}
          <span className="flex-1 text-sm font-medium truncate">
            {node.label}
          </span>

          {/* Count Badge */}
          {node.count !== undefined && node.count > 0 && (
            <span className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">
              {node.count}
            </span>
          )}

          {/* Status Icon */}
          {getStatusIcon(node.status)}

          {/* Action Menu */}
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                // Handle node actions
              }}
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="ml-4">
            {node.children!.map(child => renderTreeNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderSearchResults = () => {
    if (!searchQuery.trim()) return null;

    return (
      <div className="p-4 border-b border-border">
        <h3 className="text-sm font-semibold text-text mb-3">
          Search Results ({searchResults.length})
        </h3>
        <div className="space-y-1">
          {searchResults.map(result => (
            <div
              key={result.id}
              className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer hover:bg-background-secondary transition-colors"
              onClick={() => handleNodeClick(result)}
            >
              <span className="text-lg">{result.icon}</span>
              <span className="flex-1 text-sm text-text-secondary truncate">
                {result.label}
              </span>
              {getStatusIcon(result.status)}
            </div>
          ))}
        </div>
      </div>
    );
  };

  useEffect(() => {
    loadBlueprintTree();
  }, [loadBlueprintTree]);

  if (loading) {
    return (
      <div className={`bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 ${className}`}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-accent-500" />
          <span className="ml-2 text-gray-600 dark:text-gray-400">Loading blueprint...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 ${className}`}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertCircle className="w-8 h-8 text-destructive mx-auto mb-2" />
            <p className="text-sm text-destructive">{error}</p>
            <button
              onClick={loadBlueprintTree}
              className="btn btn-primary mt-2 px-3 py-1 text-sm"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!treeData) {
    return null;
  }

  return (
    <div className={`bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col h-full ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
          Project Blueprint
        </h2>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search blueprint..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      {/* Search Results */}
      {renderSearchResults()}

      {/* Tree Navigation */}
      <div className="flex-1 overflow-y-auto p-4">
        {searchQuery.trim() ? (
          <div className="text-center py-8">
            <Search className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {searchResults.length === 0 ? 'No results found' : 'Search results shown above'}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {treeData.root.children?.map(node => renderTreeNode(node))}
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex gap-2">
          <button
            onClick={() => {
              // Handle add new item
            }}
            className="btn btn-primary flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm"
          >
            <Plus className="w-4 h-4" />
            Add Item
          </button>
          <button
            onClick={() => {
              // Handle refresh
              loadBlueprintTree();
            }}
            className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <Eye className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}